import { useAuth } from "../../authentication/contexts/useAuth";
import React, { useEffect, useMemo, useState } from "react";
import CompanyCountry from "../../company/models/CompanyCountry";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import findCompanyMemberStatuses from "../graphql/queries/findCompanyMemberStatuses.graphql";
import triggerCompanyMemberStatusesUpdate from "../graphql/mutations/triggerCompanyMemberStatusesUpdate.graphql";
import userIdentity from "../qraphql/queries/userIdentity.graphql";
import { arrayToObject, removeElement } from "../../common/utils/arrayUtils";
import Table from "../../common/components/elements/Table";
import I18n from "i18n-js";
import PropTypes from "prop-types";
import { CompanySyncStatusesTable } from "./CompanySyncStatusesTable";
import { CompanyMemberStatusCell } from "./CompanyMemberStatusCell";

CompanySyncMembersTable.propTypes = {
  allData: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  uuidKey: PropTypes.string,
  companyData: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  ...Table.propTypes,
};

export function CompanySyncMembersTable({
  allData = [],
  uuidKey = "uuid",
  companyData,
  ...props
}) {
  const { user } = useAuth();
  const usesTrackdechets = useMemo(
    () => new CompanyCountry(user.companyCountry).usesTrackdechets(),
    [user],
  );

  const { data: userIdentityData } = useQuery(userIdentity, {
    variables: {
      userUuid: user.uuid,
    },
    skip: !usesTrackdechets,
  });
  const userHasLinkedAccount = !!userIdentityData?.userIdentity;
  const uuids = useMemo(
    () => allData.map((item) => item[uuidKey]),
    [allData, uuidKey],
  );

  const [fetch, { data, loading, error, refetch }] = useLazyQuery(
    findCompanyMemberStatuses,
  );

  useEffect(() => {
    if (usesTrackdechets) {
      fetch({ variables: { uuids } });
    }
  }, [fetch, usesTrackdechets, uuids]);

  const { refreshMemberStatus, isItemRefreshing, hasItemError } =
    useRefreshMemberStatus(refetch);

  const trackdechetsMemberInfo = useMemo(
    () => arrayToObject(data?.trackdechetsCompaniesMemberStatus || [], "uuid"),
    [data],
  );

  if (!usesTrackdechets) {
    return <Table {...props} />;
  }

  function renderMemberCell(uuid) {
    const info = trackdechetsMemberInfo[uuid];
    return (
      <CompanyMemberStatusCell
        key="td-sync-member-cell"
        loading={loading || isItemRefreshing(uuid)}
        error={error || hasItemError(uuid)}
        isMember={info?.isMember ?? null}
        lastUpdatedAt={
          info?.lastUpdatedAt ? new Date(info.lastUpdatedAt) : null
        }
        refresh={() => refreshMemberStatus(uuid)}
        userHasLinkedAccount={userHasLinkedAccount}
      />
    );
  }

  return (
    <CompanySyncStatusesTable
      {...props}
      target="CLIENT"
      uuidKey={uuidKey}
      allData={allData}
      emptyContent={I18n.t("pages.my_enterprise.list.empty")}
      data={companyData}
      headData={props.headData}
      rowData={props.rowData}
      showTrackdechetsInfo
      renderMemberCell={renderMemberCell}
      userHasLinkedAccount={userHasLinkedAccount}
      skipChunk
    />
  );
}

/**
 * Hook providing logic for refreshing a single status item in the list,
 * by triggering an async update and waiting a bit before fetching the new value.
 */
function useRefreshMemberStatus(refetch) {
  const [refreshingRows, setRefreshingRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [triggerUpdate] = useMutation(triggerCompanyMemberStatusesUpdate);

  return {
    isItemRefreshing: (uuid) => refreshingRows.includes(uuid),
    hasItemError: (uuid) => errors.includes(uuid),
    refreshMemberStatus: async (uuid) => {
      setRefreshingRows(refreshingRows.concat(uuid));
      setErrors(removeElement(errors, uuid));

      const options = {
        variables: {
          uuids: [uuid],
        },
      };

      try {
        // trigger async update of the status
        await triggerUpdate(options);

        // wait a second
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // attempt to fetch the updated result,
        // which will update Apollo's cached data for this item:
        await refetch({
          ...options,
          fetchPolicy: "network-only",
        });
      } catch {
        setErrors(errors.concat(uuid));
      } finally {
        setRefreshingRows(removeElement(refreshingRows, uuid));
      }
    },
  };
}
